import { useState, useRef, useEffect } from "react";
import { Plus, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useFoodLogs } from "@/hooks/useFoodLogs";
import { useFoods } from "@/hooks/useFoods";
import { CATEGORY_COLORS } from "@/constants/foodUI";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { CategoryKey } from "@/types/ui";

interface SmartFABProps {
  onQuickAdd: (foodId: string, foodName: string) => void;
  onOpenFullSheet: () => void;
}

export function SmartFAB({ onQuickAdd, onOpenFullSheet }: SmartFABProps) {
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const { getRecentFoods } = useFoodLogs();
  const { foods } = useFoods();
  const navigate = useNavigate();
  const longPressTimer = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showQuickMenu) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowQuickMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showQuickMenu]);

  const recentFoods = getRecentFoods()
    .slice(0, 3)
    .map(log => {
      const food = foods.find(f => f.id === log.food_id);
      return food ? { id: food.id, name: food.name, category: food.category } : null;
    })
    .filter(Boolean);

  const handleTouchStart = () => {
    longPressTimer.current = setTimeout(() => {
      if (recentFoods.length > 0) {
        setShowQuickMenu(true);
      }
    }, 300);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleClick = () => {
    if (!showQuickMenu && recentFoods.length > 0) {
      // Do nothing on click, let long press handle it
      return;
    }
    onOpenFullSheet();
  };

  const handleQuickSelect = (foodId: string, foodName: string) => {
    setShowQuickMenu(false);
    onQuickAdd(foodId, foodName);
  };

  return (
    <div ref={containerRef} className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40">
      {/* Quick menu */}
      {showQuickMenu && recentFoods.length > 0 && (
        <Card className="absolute bottom-16 right-0 p-2 space-y-2 min-w-[200px] animate-scale-in shadow-lg">
          <div className="text-xs font-medium text-muted-foreground px-2 pb-1 border-b">
            Sist brukt
          </div>
          {recentFoods.map((food) => {
            if (!food) return null;
            const categoryColor = CATEGORY_COLORS[(food.category as CategoryKey) || 'other'];
            return (
              <Button
                key={food.id}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-left h-auto py-2"
                onClick={() => handleQuickSelect(food.id, food.name)}
              >
                <div
                  className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                  style={{ backgroundColor: categoryColor }}
                />
                <span className="text-sm truncate">{food.name}</span>
              </Button>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              setShowQuickMenu(false);
              onOpenFullSheet();
            }}
          >
            Se alle
          </Button>
          <Separator className="my-1" />
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-left h-auto py-2 text-muted-foreground"
            onClick={() => {
              setShowQuickMenu(false);
              navigate("/kontakt");
            }}
          >
            <MessageSquare className="h-3.5 w-3.5 mr-2" />
            <span className="text-sm">Kontakt oss</span>
          </Button>
        </Card>
      )}

      {/* FAB button */}
      <Button
        size="lg"
        className="h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90 relative"
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
      >
        <Plus className="h-6 w-6" />
      </Button>

      {/* Hint for first-time users */}
      {recentFoods.length > 0 && !showQuickMenu && (
        <div className="absolute -top-8 right-0 text-xs text-muted-foreground whitespace-nowrap animate-fade-in">
          Hold for hurtigvalg
        </div>
      )}
    </div>
  );
}
